import React, { useMemo, useCallback } from "react"
import uniq from "lodash.uniq"
import { Table, Space } from "antd"
import { LinkOutlined, EnterOutlined } from "@ant-design/icons"
import { useOutletContext } from "react-router-dom"

import { getCurrencyValue } from "@components/Amount"
import { PROFILE_TYPES_LABELS } from "@components/Domain"

import InvestorColumn from "../../InvestorColumn"
import computeInvestmentName from "../../helpers/computeInvestmentName"

const LABEL_NAME = "Investment"
const LABEL_TYPE = "Profile Type"
const LABEL_CLASS = "Class"

const useColumns = ({
  sourceProjectId,
  sourceFiles,
  targetFiles,
  sourceInvestments,
  targetInvestments,
  sourceTransactions,
  targetTransactions,
}) => {
  const { computeAccountFullname } = useOutletContext()

  const sourceProjectTierIds = useMemo(() =>
    uniq(
      [ ...sourceInvestments, ...targetInvestments ]
        .map(item => item.investments)
        .reduce((cur, acc) => [...cur, ...acc], [])
        .map(investment => investment.projectTierId)
    )
  , [ sourceInvestments, targetInvestments ])

  const getInvestmentTierAmount = (tierId, { investments }) => {
    const tierInvestment = investments
      .find(({ projectTierId }) => projectTierId === tierId)

    if (!tierInvestment) {
      return
    }

    const { amount } = tierInvestment

    return getCurrencyValue(amount)
  }

  const getInvestmentAttachmentsCount = useCallback((investment) => {
    const isSouceInvestment = investment.projectId === sourceProjectId

    if (isSouceInvestment) {
      return [ ...sourceFiles, ...sourceTransactions ]
        .filter(({ investmentId }) => investmentId === investment.id)
        .length
    }

    return [ ...targetFiles, ...targetTransactions ]
      .filter(({ investmentId }) => investmentId === investment.id)
      .length
  }, [
    sourceProjectId,
    sourceFiles,
    targetFiles,
    sourceTransactions,
    targetTransactions,
  ])

  const columns = useMemo(() => {
    return [
      {
        key: "name",
        title: LABEL_NAME,
        dataIndex: "name",
        render: (value, { children, ...investment }) => {
          const name = computeInvestmentName(computeAccountFullname, investment)
          const isSourcenvestment = investment.projectId === sourceProjectId

          if (!isSourcenvestment) {
            return (
              <Space>
                <EnterOutlined style={{ transform: 'scaleX(-1) translateY(-3px)' }} />

                <InvestorColumn
                  name={<>{name}</>}
                  isBold={false}
                  investment={investment}
                />
              </Space>
            )
          }

          return (
            <InvestorColumn
              name={<>{name}</>}
              isBold={true}
              investment={investment}
            />
          )
        },
      },
      {
        key: "profileType",
        title: LABEL_TYPE,
        dataIndex: "profileType",
        render: value => PROFILE_TYPES_LABELS[value]
      },
      ...(
        sourceProjectTierIds
          .map(tierId => ({
            key: "tierId",
            title: `${LABEL_CLASS} ${tierId}`,
            dataIndex: tierId,
            render: (_, investment) => getInvestmentTierAmount(tierId, investment)
          }))
      ),
      {
        key: "attachmentsCount",
        align: "center",
        title: <LinkOutlined />,
        dataIndex: "_attachmentsCount",
        render: (_, investment) => getInvestmentAttachmentsCount(investment)
      },
      Table.EXPAND_COLUMN,
    ]
  }, [
    sourceProjectId,
    sourceProjectTierIds,
    computeAccountFullname,
    getInvestmentAttachmentsCount,
  ])

  return columns
}

export default useColumns
